import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // status: 'checking' | 'authenticated' | 'anonymous'
  const [status, setStatus] = useState("checking");
  const [user, setUser] = useState(null);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("fstudy_token");
    if (!token) {
      setUser(null);
      setStatus("anonymous");
      return;
    }
    // Fase 14 · Inactividad persistida: si el último `last_activity` es >7 min
    // atrás, cerrar sesión INMEDIATO antes de resolver /auth/me. Atrapa el caso
    // "usuario dejó abierto >7min y refrescó" en cualquier entorno.
    const INACTIVITY_MS = 7 * 60 * 1000;
    const lastAct = parseInt(localStorage.getItem("fstudy_last_activity") || "0", 10);
    if (lastAct && Date.now() - lastAct > INACTIVITY_MS) {
      localStorage.removeItem("fstudy_token");
      localStorage.removeItem("fstudy_last_activity");
      localStorage.setItem("fstudy_login_flash", "FStudy: Sesión cerrada por inactividad.");
      setUser(null);
      setStatus("anonymous");
      // NO usar window.location.href aquí — ProtectedRoute con status=anonymous
      // hace <Navigate to='/login'> vía react-router, que preserva el flash del
      // localStorage para que Login.jsx pueda leerlo y mostrarlo. Un hard reload
      // aquí consumiría el flash antes de que Login pueda leerlo (race).
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setStatus("authenticated");
      // Al cargar sesión válida, sembrar last_activity para arrancar el conteo
      if (!localStorage.getItem("fstudy_last_activity")) {
        localStorage.setItem("fstudy_last_activity", String(Date.now()));
      }
    } catch (e) {
      localStorage.removeItem("fstudy_token");
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("fstudy_token", data.token);
      localStorage.setItem("fstudy_last_activity", String(Date.now()));
      setUser(data.user);
      setStatus("authenticated");
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      // ignore
    }
    localStorage.removeItem("fstudy_token");
    localStorage.removeItem("fstudy_last_activity");
    setUser(null);
    setStatus("anonymous");
  }, []);

  const hasModule = useCallback(
    (moduleKey) => {
      if (!user) return false;
      if (user.role === "master") return true;
      return Boolean(user.modules?.[moduleKey]);
    },
    [user]
  );

  const isMaster = user?.role === "master";

  return (
    <AuthContext.Provider value={{ status, user, login, logout, hasModule, isMaster, reload: loadMe, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
